import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Spin, Typography, Alert, message } from 'antd';
import AppConfig from '../../config/AppConfig';
import { AuthenticationService } from '../../api/Auth';
import I18n from '../../common/I18n';
import { CompanyLogo } from '../common/LogisticsLoadingScreen';

const { Title } = Typography;

/** Only allow in-app paths (avoid open redirects). */
function safeReturnPath(returnTo) {
  if (!returnTo || typeof returnTo !== 'string') return '/';
  if (!returnTo.startsWith('/') || returnTo.startsWith('//')) return '/';
  return returnTo;
}

const formStyle = {
  background: 'rgba(255, 255, 255, 0.9)',
  padding: '20px',
  borderRadius: '10px',
  maxWidth: '400px',
  width: '100%',
  boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.2)',
};

export const SamlCallbackPage = ({ history }) => {
  const [ error, setError ] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const errParam = params.get('error');
      const returnTo = safeReturnPath(params.get('returnTo'));

      if (errParam) {
        setError(I18n.t('auth_messages.sso_error_generic'));
        return;
      }
      if (!code) {
        setError(I18n.t('auth_messages.sso_no_code'));
        return;
      }

      const base = (AppConfig.oktaSamlUri || '').replace(/\/$/, '');
      if (!base) {
        setError(I18n.t('auth_messages.sso_saml_uri_missing'));
        return;
      }

      try {
        const res = await fetch(`${base}/api/auth/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });

        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(body.error || I18n.t('auth_messages.sso_code_exchange_failed'));
        }

        const { token: samlJwt } = body;
        if (!samlJwt) {
          throw new Error(I18n.t('auth_messages.sso_code_exchange_failed'));
        }
        if (cancelled) return;

        // Keep for logout: POST okta-saml-service /api/auth/logout (Bearer) → Okta SLO URL
        try {
          sessionStorage.setItem('fe_saml_sp_jwt', samlJwt);
        } catch (e) {
          /* ignore quota / private mode */
        }

        const sessionResp = await AuthenticationService.samlSession(samlJwt);
        if (sessionResp.success && sessionResp.data) {
          localStorage.setItem('auth', JSON.stringify(sessionResp.data));
          message.success(I18n.t('auth_messages.login_success'));
          history.replace(returnTo);
        } else {
          throw new Error(sessionResp.error || I18n.t('auth_messages.error_msg'));
        }
      } catch (e) {
        if (!cancelled) {
          setError((e && e.message) || I18n.t('auth_messages.error_msg'));
        }
      }
    }

    run();
    return () => { cancelled = true; };
  }, [ history ]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: '#f5f5f5',
      }}
      data-testid="saml-callback-page"
    >
      <div style={ formStyle }>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <CompanyLogo style={{ width: '65%', height: 'auto' }} />
        </div>
        { error ? (
          <>
            <Alert
              type="error"
              message={ I18n.t('auth_messages.sso_error_title') }
              description={ error }
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Title level={ 4 } style={{ textAlign: 'center', fontWeight: 600 }}>
              <a href="/login">{ I18n.t('auth_messages.back') }</a>
            </Title>
          </>
        ) : (
          <>
            <Spin size="large" style={{ display: 'block', margin: '0 auto 20px' }} />
            <Title level={ 4 } style={{ textAlign: 'center', fontWeight: 600 }}>
              { I18n.t('auth_messages.sso_completing_message') }
            </Title>
            <p style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14, margin: 0, textAlign: 'center' }}>
              { I18n.t('auth_messages.sso_completing_hint') }
            </p>
          </>
        ) }
      </div>
    </div>
  );
};

export default withRouter(SamlCallbackPage);